import { ReactNode } from "react";
import { Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { GripVertical } from "lucide-react";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import { ChevronRight } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import { BaseStageRow } from "./types";

interface Props<T extends BaseStageRow> {
  item: T;
  index: number;
  color: string;
  badges?: ReactNode;
  rowActions?: ReactNode;
  isDragDisabled?: boolean;
  isLast?: boolean;
  // Optional override for click navigation. Defaults to `${currentPath}/stages/${id}`.
  onRowClick?: (item: T) => void;
}

const DraggableStageRow = <T extends BaseStageRow>({
  item,
  index,
  color,
  badges,
  rowActions,
  isDragDisabled = false,
  isLast = false,
  onRowClick
}: Props<T>) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    if (onRowClick) {
      onRowClick(item);
      return;
    }
    navigate(`${location.pathname}/stages/${item.id}${location.search}`);
  };

  const content = (provided: any, snapshot: any, isClone: boolean = false) => (
    <div
      ref={isClone ? null : provided.innerRef}
      {...provided.draggableProps}
      data-test={`stage-row-${item.id}`}
      className={cn(
        "group w-full flex flex-col items-start justify-center cursor-pointer h-[88px]",
        "max-w-[800px] pl-1 pr-4 py-4 gap-4",
        "bg-surface-widget-card",
        !isLast && "border-b border-border-border1",
        "hover:bg-surface-hover-soft transition-colors duration-200",
        snapshot.isDragging && [
          "border-t border-b border-border-border1",
          "bg-surface-focus-selected",
          "shadow-[0px_12px_24px_-4px_rgba(0,0,0,0.20),0px_0px_2px_-2px_rgba(0,0,0,0.18)]",
          "relative z-50"
        ]
      )}
      style={provided.draggableProps.style}
      onClick={handleClick}
    >
      <div className="flex flex-row w-full justify-between items-center">
        <div className="flex flex-row items-center w-full gap-0">
          <div
            {...provided.dragHandleProps}
            className={cn(
              "px-0 cursor-grab active:cursor-grabbing",
              "opacity-0 group-hover:opacity-100 transition-opacity"
            )}
          >
            {!isDragDisabled && (
              <GripVertical className="h-4 w-4 text-secondary" />
            )}
          </div>
          <div className="flex gap-4">
            <div
              className="min-w-2 w-2 h-14 rounded-xl"
              style={{ backgroundColor: color }}
            />
            <div className="flex flex-col w-full justify-center">
              <Text variant="body">{item.name}</Text>
              {badges && <div className="flex gap-2">{badges}</div>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {rowActions}
          <IconButton
            variant="iconNoBg"
            className={cn(
              "opacity-0 transition-all duration-200 ease-in-out group-hover:opacity-100"
            )}
            size="lg"
            iconColor="primary"
          >
            <ChevronRight />
          </IconButton>
        </div>
      </div>
    </div>
  );

  return (
    <Draggable
      draggableId={item.id.toString()}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <>
          {content(provided, snapshot)}
          {snapshot.isDragging &&
            createPortal(content(provided, snapshot, true), document.body)}
        </>
      )}
    </Draggable>
  );
};

export default DraggableStageRow;
